import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { CapTableSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineCapSelect/CapTableSelect';
import { TableFilterCheckboxSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/TableFilterCheckboxSelect';
import { TextInputFilter } from '@dealstack/frontend/shared/components/Table/MantineTable/TextInputFilter/TextInputFilter';
import {
	ExtendedColumn,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Flex, HoverCard, Text, FlexProps } from '@mantine/core';
import styled from 'styled-components';
import { ITextProps } from '@dealstack/frontend/shared/components/fundament/Text/Text.types';
import { ColumnStatusDefinition } from './StatusColumn.types';

type Props<
	T extends RecordAny,
	S extends readonly ColumnStatusDefinition[] = ColumnStatusDefinition[],
> = ExtendedColumnDef<T> & {
	accessorKey?: keyof T;
	showFooter?: boolean;
	showFilter?: boolean;
	footerValue?: Partial<Record<S[number]['status'], number>>;
	text?: string;
	footerText?: string;
	filterText?: string;
	headerText?: string;
	cellTextProps?: ITextProps;
	enableSorting?: boolean;
	mantineFilterMultiSelectProps?: {
		data: { label: string; value: string }[];
	};
	mantineFilterSelectProps?: {
		data: { label: string; value: string }[];
	};
	statusDefinition: S;
};

const StatusColumnFooter = function <
	T extends RecordAny,
	S extends readonly ColumnStatusDefinition[],
>(columnDefinition: Props<T, S>) {
	if (!columnDefinition.showFooter) {
		return undefined;
	}

	if (columnDefinition.footer) {
		return columnDefinition.footer;
	}

	if (!columnDefinition.footerValue) {
		return undefined;
	}

	// Show an entry for every known status — unseen statuses display a 0 count
	// so the footer layout stays stable across filter changes.
	const counts = (columnDefinition.footerValue ?? {}) as Record<string, number | undefined>;
	const items = columnDefinition.statusDefinition.map((def) => ({
		status: def.status,
		color: def.color,
		text: def.text,
		value: counts[def.status] ?? 0,
	}));

	return (
		<DetailsDiv>
			<HoverCard position="bottom" withinPortal closeDelay={0}>
				<HoverCard.Target>
					<InfoDiv gap="xs">
						{items.map(({ status, color, value }) => (
							<Flex align="center" gap="0.25rem" key={`${status}-target`}>
								<div
									style={{
										width: 6,
										height: 6,
										backgroundColor: color,
										borderRadius: '50%',
										padding: '0.06rem',
									}}
								/>
								<Text fw={600}>{value}</Text>
							</Flex>
						))}
					</InfoDiv>
				</HoverCard.Target>
				<HoverCard.Dropdown p="0.5rem">
					<Flex gap="0.75rem">
						{items.map(({ status, color, text, value }) => (
							<Flex
								align="start"
								direction="column"
								key={`${columnDefinition.text}-${status}`}
							>
								<Text fw={600} fs="0.875rem">
									{value}
								</Text>
								<Flex align="center" gap="0.25rem">
									<div
										style={{
											width: 6,
											height: 6,
											backgroundColor: color,
											borderRadius: '50%',
											padding: '0.06rem',
										}}
									/>
									<Text fs="0.75rem">{text}</Text>
								</Flex>
							</Flex>
						))}
					</Flex>
				</HoverCard.Dropdown>
			</HoverCard>
		</DetailsDiv>
	);
};

export const createStatusColumn = <
	T extends RecordAny,
	const S extends readonly ColumnStatusDefinition[],
>(
	props: Props<T, S>
): ExtendedColumnDef<T> => {
	const {
		cellTextProps = {},
		filterText,
		headerText,
		enableSorting = true,
		mantineFilterMultiSelectProps,
		mantineFilterSelectProps,
		...columnDef
	} = props;

	const result = {
		...columnDef,
		header: headerText ? createSortableTextHeader(headerText, enableSorting) : columnDef.header,
		enableSorting,
		accessorKey: columnDef.accessorKey ?? columnDef.id ?? 'text',
		cell:
			columnDef.cell ??
			(({ cell }) => (
				<Flex align="center" justify="start" w="100%" miw="0">
					<Text
						size="B6Medium"
						style={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
						{...cellTextProps}
					>
						{cell.getValue<string>()}
					</Text>
				</Flex>
			)),
		footer: StatusColumnFooter(props),
		filter: columnDef.showFilter
			? ({ column }) => {
					if (columnDef.filterVariant === 'multi-select') {
						return (
							<TableFilterCheckboxSelect
								column={column as ExtendedColumn<unknown>}
								filterText={filterText}
								multiple
							/>
						);
					}

					if (columnDef.filterVariant === 'select') {
						return (
							<CapTableSelect
								column={column as ExtendedColumn<unknown>}
								filterText={filterText}
							/>
						);
					}

					if (columnDef.filter) {
						return columnDef.filter({ column });
					}

					return <TextInputFilter column={column} filterText={filterText} />;
				}
			: undefined,
		mantineFilterMultiSelectProps,
		mantineFilterSelectProps,
	} as ExtendedColumnDef<T>;

	return result;
};

const DetailsDiv = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	font-size: 14px;
	line-height: 20px;
`;

const InfoDiv = styled(Flex)<FlexProps>`
	font-weight: 600;
`;
